/**
 * StatusRow - A single color-coded status row in the compact card.
 *
 * Matches the concept HTML pattern:
 *   [Icon 14px] [Text flex-1] [Action: button + divider + chevron dropdown]
 *
 * OK rows: subtle chevron only.
 * Warning/critical rows: primary action button + chevron dropdown with secondary actions.
 */

import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { StatusRowSeverity } from '@/types/aircraft-card'
import { STATUS_ROW_ICON_MAP } from '@/lib/utils/status-row-icons'

// ── Color mapping ──────────────────────────────────────

interface SeverityColors {
  bg: string
  text: string
  border: string
  progressBg: string
  progressBar: string
  divider: string
}

const SEVERITY_COLORS: Record<StatusRowSeverity, SeverityColors> = {
  ok: {
    bg: 'bg-green-50 dark:bg-green-950',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    progressBg: 'bg-green-200 dark:bg-green-800',
    progressBar: 'bg-green-500 dark:bg-green-400',
    divider: 'bg-green-300 dark:bg-green-700',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    progressBg: 'bg-blue-200 dark:bg-blue-800',
    progressBar: 'bg-blue-500 dark:bg-blue-400',
    divider: 'bg-blue-300 dark:bg-blue-700',
  },
  notice: {
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    text: 'text-yellow-700 dark:text-yellow-400',
    border: 'border-yellow-200 dark:border-yellow-800',
    progressBg: 'bg-yellow-200 dark:bg-yellow-800',
    progressBar: 'bg-yellow-500 dark:bg-yellow-400',
    divider: 'bg-yellow-300 dark:bg-yellow-700',
  },
  warning: {
    bg: 'bg-orange-50 dark:bg-orange-950',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    progressBg: 'bg-orange-200 dark:bg-orange-800',
    progressBar: 'bg-orange-500 dark:bg-orange-400',
    divider: 'bg-orange-300 dark:bg-orange-700',
  },
  critical: {
    bg: 'bg-red-50 dark:bg-red-950',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    progressBg: 'bg-red-200 dark:bg-red-800',
    progressBar: 'bg-red-500 dark:bg-red-400',
    divider: 'bg-red-300 dark:bg-red-700',
  },
}

// ── Dropdown action type ───────────────────────────────

export interface StatusRowDropdownItem {
  label: string
  onClick: () => void
  destructive?: boolean
}

// ── Props ──────────────────────────────────────────────

export interface StatusRowProps {
  severity: StatusRowSeverity
  iconName: string
  text: string
  /** Optional secondary icon rendered inline before the text */
  textIconName?: string
  progressValue?: number
  tooltipLines?: string[]
  /** Label for the primary action button (warning/critical rows) */
  actionLabel?: string
  /** Callback for the primary action button */
  onAction?: () => void
  /** Multiple inline action buttons (takes precedence over actionLabel/onAction) */
  actionButtons?: { label: string; onClick: () => void }[]
  /** Secondary actions in the chevron dropdown */
  dropdownItems?: StatusRowDropdownItem[]
  /** Callback for clicking the row body */
  onRowClick?: () => void
  /** Whether this is the last row (no bottom border) */
  isLast?: boolean
}

export function StatusRow({
  severity,
  iconName,
  text,
  textIconName,
  progressValue,
  tooltipLines,
  actionLabel,
  onAction,
  actionButtons,
  dropdownItems,
  onRowClick,
  isLast,
}: StatusRowProps) {
  const colors = SEVERITY_COLORS[severity]
  const Icon = STATUS_ROW_ICON_MAP[iconName] || AlertTriangle
  const TextIcon = textIconName ? STATUS_ROW_ICON_MAP[textIconName] : null

  const isClickable = !!onRowClick
  const hasMultiAction = actionButtons && actionButtons.length > 0
  const hasAction = !hasMultiAction && !!actionLabel && !!onAction
  const hasDropdown = dropdownItems && dropdownItems.length > 0

  const rowContent = (
    <div
      className={`flex items-center gap-1.5 px-3 py-1 text-[11px] font-medium ${colors.bg} ${colors.text} ${
        !isLast ? `border-b ${colors.border}` : ''
      }`}
    >
      {/* Clickable row body */}
      <div
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? onRowClick : undefined}
        onKeyDown={
          isClickable
            ? (e) => { if (e.key === 'Enter' || e.key === ' ') onRowClick!() }
            : undefined
        }
        className={`flex items-center gap-1.5 flex-1 min-w-0 ${
          isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        }`}
      >
        <Icon className="w-3.5 h-3.5 shrink-0" />
        {TextIcon && <TextIcon className="w-3 h-3 shrink-0 opacity-70" />}
        <div className="flex-1 min-w-0">
          <span className="block truncate">{text}</span>
          {progressValue != null && (
            <div className={`mt-0.5 h-[3px] w-full rounded-full ${colors.progressBg}`}>
              <div
                className={`h-full rounded-full transition-all ${colors.progressBar}`}
                style={{ width: `${progressValue}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Action area */}
      {hasMultiAction ? (
        <div className="flex items-center gap-px shrink-0">
          {actionButtons!.map((btn, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); btn.onClick() }}
              className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/60 dark:bg-black/20 hover:bg-white/90 dark:hover:bg-black/40 transition-colors"
            >
              {btn.label}
            </button>
          ))}
        </div>
      ) : hasAction ? (
        <div className="flex items-center shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAction!()
            }}
            className="px-1.5 py-0.5 text-[10px] font-semibold rounded-l bg-white/60 dark:bg-black/20 hover:bg-white/90 dark:hover:bg-black/40 transition-colors"
          >
            {actionLabel}
          </button>
          {hasDropdown && (
            <>
              <div className={`w-px h-4 ${colors.divider}`} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="px-0.5 py-0.5 rounded-r bg-white/60 dark:bg-black/20 hover:bg-white/90 dark:hover:bg-black/40 transition-colors"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {dropdownItems.map((item, idx) => (
                    <DropdownMenuItem
                      key={idx}
                      onClick={item.onClick}
                      className={item.destructive ? 'text-destructive focus:text-destructive' : ''}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      ) : isClickable ? (
        <ChevronRight
          className="w-3 h-3 shrink-0 opacity-50 cursor-pointer"
          onClick={onRowClick}
        />
      ) : null}
    </div>
  )

  // Wrap with tooltip if tooltipLines provided
  if (tooltipLines && tooltipLines.length > 0) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{rowContent}</TooltipTrigger>
          <TooltipContent side="top" className="text-xs space-y-0.5 max-w-[220px]">
            {tooltipLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return rowContent
}
